import { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Save, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { logError } from "@/lib/log";

/**
 * Dialog used by both gestor (AttendanceList) and instructor
 * (InstructorScreen → current course attendances) to edit administrative
 * fields of an attendance.
 *
 * Props:
 *  - attendance: { id, name, surname, dni, profession } | null
 *  - open: boolean (controlled)
 *  - onClose: () => void
 *  - onSubmit: async (id, payload) => updatedAttendance
 *      Must throw on error so the dialog can keep open with the error toast.
 *  - title?: string
 */
export default function AttendanceEditDialog({
  attendance,
  open,
  onClose,
  onSubmit,
  title = "Editar datos del alumno",
}) {
  const [form, setForm] = useState({
    name: "",
    surname: "",
    dni: "",
    profession: "",
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (attendance) {
      setForm({
        name: attendance.name || "",
        surname: attendance.surname || "",
        dni: attendance.dni || "",
        profession: attendance.profession || "",
      });
    }
  }, [attendance]);

  const handleSave = async () => {
    if (!attendance) return;
    if (!form.name.trim() || !form.surname.trim() || !form.dni.trim()) {
      toast.error("Nombre, apellidos y DNI son obligatorios.");
      return;
    }
    setSaving(true);
    try {
      await onSubmit(attendance.id, {
        name: form.name.trim(),
        surname: form.surname.trim(),
        dni: form.dni.trim().toUpperCase(),
        profession: form.profession.trim(),
      });
      toast.success("Datos actualizados.");
      onClose?.();
    } catch (e) {
      logError("No se pudo actualizar la asistencia", e);
      toast.error(
        e?.response?.data?.detail || "No se pudo actualizar."
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && !saving && onClose?.()}>
      <DialogContent className="sm:max-w-lg" data-testid="attendance-edit-dialog">
        <DialogHeader>
          <DialogTitle className="heading-font text-2xl">{title}</DialogTitle>
          <DialogDescription>
            Sólo se pueden editar los datos administrativos. La firma y los
            consentimientos RGPD quedan fijados como prueba legal.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-zinc-700 mb-2">
                Nombre *
              </label>
              <Input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value.toUpperCase() })}
                className="h-11 border-2 border-zinc-300 focus-visible:border-zinc-900 focus-visible:ring-0"
                data-testid="attendance-edit-name"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-zinc-700 mb-2">
                Apellidos *
              </label>
              <Input
                value={form.surname}
                onChange={(e) => setForm({ ...form, surname: e.target.value.toUpperCase() })}
                className="h-11 border-2 border-zinc-300 focus-visible:border-zinc-900 focus-visible:ring-0"
                data-testid="attendance-edit-surname"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-zinc-700 mb-2">
                DNI *
              </label>
              <Input
                value={form.dni}
                onChange={(e) =>
                  setForm({ ...form, dni: e.target.value.toUpperCase() })
                }
                maxLength={9}
                className="h-11 border-2 border-zinc-300 focus-visible:border-zinc-900 focus-visible:ring-0 tracking-wider"
                data-testid="attendance-edit-dni"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-zinc-700 mb-2">
              Profesión (obligatoria sólo en Castilla y León)
            </label>
            <Input
              value={form.profession}
              onChange={(e) =>
                setForm({ ...form, profession: e.target.value.toUpperCase() })
              }
              placeholder="—"
              className="h-11 border-2 border-zinc-300 focus-visible:border-zinc-900 focus-visible:ring-0"
              data-testid="attendance-edit-profession"
            />
          </div>
        </div>

        <DialogFooter className="gap-2">
          <Button
            variant="outline"
            onClick={() => !saving && onClose?.()}
            disabled={saving}
            className="border border-zinc-300"
            data-testid="attendance-edit-cancel"
          >
            Cancelar
          </Button>
          <Button
            onClick={handleSave}
            disabled={saving}
            className="bg-zinc-950 hover:bg-zinc-800 text-white"
            data-testid="attendance-edit-save"
          >
            {saving ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Guardando…
              </>
            ) : (
              <>
                <Save className="h-4 w-4 mr-2" />
                Guardar cambios
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
